import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { CreditCard, Mail, RefreshCw, CheckCircle2, AlertCircle, Trash2, Send, Database, Copy } from "lucide-react";

type IntegrationRow = {
  provider: string;
  title: string;
  description: string;
  configured: boolean;
  source: "database" | "environment" | "missing";
  status?: string | null;
  lastError?: string | null;
  lastTestedAt?: string | null;
  updatedAt?: string | null;

  // stripe
  publishableKey?: string;
  webhookConfigured?: boolean;
  secretKeyHint?: string;
  webhookSecretHint?: string;
  webhookUrl?: string;

  // email
  emailProvider?: "sendgrid" | "smtp" | "noop";
  fromEmail?: string;
  fromName?: string;
  sendGridKeyHint?: string;
  smtpHost?: string;
  smtpPort?: number;
  smtpUser?: string;
  smtpPasswordHint?: string;
  smtpUseSsl?: boolean;
};

function StatusBadge({ row }: { row: IntegrationRow }) {
  if (!row.configured) return <Badge variant="outline">Not configured</Badge>;
  if (row.status === "error") return <Badge variant="destructive">Error</Badge>;
  return (
    <Badge variant="default" className="bg-emerald-600 hover:bg-emerald-600">
      <CheckCircle2 className="w-3 h-3 mr-1" /> Active
    </Badge>
  );
}

function SourceTag({ src }: { src: IntegrationRow["source"] }) {
  if (src === "database")
    return (
      <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
        <Database className="w-3 h-3" /> stored in database
      </span>
    );
  if (src === "environment")
    return <span className="text-xs text-muted-foreground">from environment variable</span>;
  return <span className="text-xs text-muted-foreground">no credentials yet</span>;
}

export default function AdminIntegrationsPage() {
  const { toast } = useToast();
  const { data, isLoading } = useQuery<IntegrationRow[]>({ queryKey: ["/api/admin/integrations"] });

  const stripeRow = data?.find((r) => r.provider === "stripe");
  const emailRow = data?.find((r) => r.provider === "email");

  return (
    <div className="container max-w-5xl mx-auto p-4 sm:p-6 space-y-6" data-testid="page-admin-integrations">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Master Admin · Integrations</h1>
        <p className="text-muted-foreground mt-1">
          Manage platform-wide credentials. Saved values are encrypted and take effect immediately — no restart needed.
        </p>
      </div>

      {isLoading && <div className="text-sm text-muted-foreground">Loading…</div>}

      {stripeRow && <StripeCard row={stripeRow} onSaved={() => queryClient.invalidateQueries({ queryKey: ["/api/admin/integrations"] })} toast={toast} />}
      {emailRow && <EmailCard row={emailRow} onSaved={() => queryClient.invalidateQueries({ queryKey: ["/api/admin/integrations"] })} toast={toast} />}
    </div>
  );
}

function StripeCard({ row, onSaved, toast }: { row: IntegrationRow; onSaved: () => void; toast: ReturnType<typeof useToast>["toast"] }) {
  const [publishable, setPublishable] = useState("");
  const [secret, setSecret] = useState("");
  const [webhook, setWebhook] = useState("");

  const save = useMutation({
    mutationFn: async () => {
      await apiRequest("PUT", "/api/admin/integrations/stripe", {
        publishableKey: publishable || null,
        secretKey: secret || null,
        webhookSecret: webhook || null,
      });
    },
    onSuccess: () => {
      toast({ title: "Stripe credentials saved" });
      setSecret("");
      setWebhook("");
      onSaved();
    },
    onError: (e: any) => toast({ title: "Save failed", description: e.message, variant: "destructive" }),
  });

  const test = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/integrations/stripe/test", {});
      return await res.json();
    },
    onSuccess: (j: any) => {
      toast({ title: "Stripe OK", description: `Account ${j.accountId} (${j.country || "?"})` });
      onSaved();
    },
    onError: (e: any) => toast({ title: "Stripe test failed", description: e.message, variant: "destructive" }),
  });

  const sync = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/integrations/stripe/sync-plans", {});
      return await res.json();
    },
    onSuccess: (j: any) => {
      const okCount = (j.results || []).filter((r: any) => r.ok).length;
      toast({ title: "Plans synced", description: `${okCount}/${(j.results || []).length} plans pushed to Stripe.` });
      onSaved();
    },
    onError: (e: any) => toast({ title: "Sync failed", description: e.message, variant: "destructive" }),
  });

  const clear = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", "/api/admin/integrations/stripe");
    },
    onSuccess: () => {
      toast({ title: "Stripe credentials cleared" });
      onSaved();
    },
  });

  return (
    <Card data-testid="card-integration-stripe">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
            <CreditCard className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <CardTitle className="text-lg flex items-center gap-2">{row.title} <StatusBadge row={row} /></CardTitle>
            <CardDescription className="mt-1">{row.description}</CardDescription>
            <div className="mt-1"><SourceTag src={row.source} /></div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {row.lastError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-xs break-all">{row.lastError}</AlertDescription>
          </Alert>
        )}

        {/* Warn when Stripe key is set but webhook secret is missing — subscription
            activation will silently 503 on every checkout.session.completed event. */}
        {row.configured && !row.webhookConfigured && (
          <Alert className="border-amber-400 bg-amber-50 dark:bg-amber-950/30">
            <AlertCircle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800 dark:text-amber-300 text-xs space-y-2">
              <p>
                <strong>Webhook secret not configured.</strong> Stripe checkout events will be rejected (HTTP 503) —
                tenants stay "pending" indefinitely after payment.
              </p>
              {row.webhookUrl && (
                <p>
                  Register this endpoint in your{" "}
                  <a
                    href="https://dashboard.stripe.com/test/webhooks"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline font-medium"
                  >
                    Stripe webhook dashboard
                  </a>
                  , then paste the signing secret (<code>whsec_…</code>) in the field below:
                </p>
              )}
              {row.webhookUrl && (
                <div className="flex items-center gap-2 bg-white dark:bg-black/30 rounded px-3 py-1.5 border border-amber-200 dark:border-amber-800 font-mono text-xs break-all">
                  <span className="flex-1 select-all">{row.webhookUrl}</span>
                  <button
                    type="button"
                    title="Copy webhook URL"
                    onClick={() => { navigator.clipboard.writeText(row.webhookUrl!); }}
                    className="shrink-0 text-amber-700 hover:text-amber-900 dark:text-amber-400 dark:hover:text-amber-200"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
              <p>
                Subscribe to: <code>checkout.session.completed</code>, <code>customer.subscription.created</code>,{" "}
                <code>customer.subscription.updated</code>, <code>customer.subscription.deleted</code>,{" "}
                <code>invoice.payment_failed</code>
              </p>
            </AlertDescription>
          </Alert>
        )}

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="stripe-pk">Publishable key</Label>
            <Input
              id="stripe-pk"
              value={publishable}
              onChange={(e) => setPublishable(e.target.value)}
              placeholder={row.publishableKey ? row.publishableKey : "pk_test_…"}
              data-testid="input-stripe-publishable"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="stripe-sk">Secret key</Label>
            <Input
              id="stripe-sk"
              type="password"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              placeholder={row.secretKeyHint || "sk_test_…"}
              data-testid="input-stripe-secret"
            />
            <p className="text-xs text-muted-foreground">Stored encrypted (AES-GCM). Leave blank to keep current.</p>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="stripe-wh">Webhook signing secret</Label>
            <Input
              id="stripe-wh"
              type="password"
              value={webhook}
              onChange={(e) => setWebhook(e.target.value)}
              placeholder={row.webhookSecretHint || "whsec_…"}
              data-testid="input-stripe-webhook"
            />
            <p className="text-xs text-muted-foreground">
              Endpoint:{" "}
              {row.webhookUrl ? (
                <code className="font-mono select-all">{row.webhookUrl}</code>
              ) : (
                <code className="font-mono">/api/webhooks/stripe</code>
              )}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="button-stripe-save">
            {save.isPending ? "Saving…" : "Save"}
          </Button>
          <Button variant="outline" onClick={() => test.mutate()} disabled={test.isPending || !row.configured} data-testid="button-stripe-test">
            <CheckCircle2 className="mr-2 h-4 w-4" />
            {test.isPending ? "Testing…" : "Test connection"}
          </Button>
          <Button variant="outline" onClick={() => sync.mutate()} disabled={sync.isPending || !row.configured} data-testid="button-stripe-sync">
            <RefreshCw className={`mr-2 h-4 w-4 ${sync.isPending ? "animate-spin" : ""}`} />
            {sync.isPending ? "Syncing…" : "Sync plans → Stripe"}
          </Button>
          {row.source === "database" && (
            <Button variant="ghost" onClick={() => clear.mutate()} disabled={clear.isPending} data-testid="button-stripe-clear">
              <Trash2 className="mr-2 h-4 w-4" />
              Clear (revert to env)
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function EmailCard({ row, onSaved, toast }: { row: IntegrationRow; onSaved: () => void; toast: ReturnType<typeof useToast>["toast"] }) {
  const [provider, setProvider] = useState<string>(row.emailProvider || "noop");
  const [fromEmail, setFromEmail] = useState(row.fromEmail || "");
  const [fromName, setFromName] = useState(row.fromName || "");
  const [sgKey, setSgKey] = useState("");
  const [smtpHost, setSmtpHost] = useState(row.smtpHost || "");
  const [smtpPort, setSmtpPort] = useState<number>(row.smtpPort || 587);
  const [smtpUser, setSmtpUser] = useState(row.smtpUser || "");
  const [smtpPass, setSmtpPass] = useState("");
  const [smtpSsl, setSmtpSsl] = useState<boolean>(row.smtpUseSsl ?? true);
  const [testTo, setTestTo] = useState("");

  const save = useMutation({
    mutationFn: async () => {
      await apiRequest("PUT", "/api/admin/integrations/email", {
        provider,
        fromEmail: fromEmail || null,
        fromName: fromName || null,
        sendGridApiKey: sgKey || null,
        smtpHost: smtpHost || null,
        smtpPort,
        smtpUser: smtpUser || null,
        smtpPassword: smtpPass || null,
        smtpUseSsl: smtpSsl,
      });
    },
    onSuccess: () => {
      toast({ title: "Email settings saved" });
      setSgKey("");
      setSmtpPass("");
      onSaved();
    },
    onError: (e: any) => toast({ title: "Save failed", description: e.message, variant: "destructive" }),
  });

  const test = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/integrations/email/test", { to: testTo });
      return await res.json();
    },
    onSuccess: (j: any) => toast({ title: "Test send", description: j.message }),
    onError: (e: any) => toast({ title: "Test failed", description: e.message, variant: "destructive" }),
  });

  const clear = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", "/api/admin/integrations/email");
    },
    onSuccess: () => {
      toast({ title: "Email settings cleared" });
      onSaved();
    },
  });

  return (
    <Card data-testid="card-integration-email">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
            <Mail className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <CardTitle className="text-lg flex items-center gap-2">{row.title} <StatusBadge row={row} /></CardTitle>
            <CardDescription className="mt-1">{row.description}</CardDescription>
            <div className="mt-1"><SourceTag src={row.source} /></div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {row.lastError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-xs break-all">{row.lastError}</AlertDescription>
          </Alert>
        )}

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="email-provider">Provider</Label>
            <Select value={provider} onValueChange={setProvider}>
              <SelectTrigger id="email-provider" data-testid="select-email-provider"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="sendgrid">SendGrid (HTTP API)</SelectItem>
                <SelectItem value="smtp">SMTP server</SelectItem>
                <SelectItem value="noop">No-op (log only — dev)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email-from">From email</Label>
            <Input id="email-from" type="email" value={fromEmail} onChange={(e) => setFromEmail(e.target.value)} placeholder="noreply@yourdomain.com" data-testid="input-email-from" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="email-from-name">From name (optional)</Label>
            <Input id="email-from-name" value={fromName} onChange={(e) => setFromName(e.target.value)} placeholder="Dotnet Bot" data-testid="input-email-from-name" />
          </div>
        </div>

        {provider === "sendgrid" && (
          <div className="space-y-1.5">
            <Label htmlFor="sg-key">SendGrid API key</Label>
            <Input
              id="sg-key"
              type="password"
              value={sgKey}
              onChange={(e) => setSgKey(e.target.value)}
              placeholder={row.sendGridKeyHint || "SG.xxxx…"}
              data-testid="input-sendgrid-key"
            />
            <p className="text-xs text-muted-foreground">Stored encrypted. Leave blank to keep current.</p>
          </div>
        )}

        {provider === "smtp" && (
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="smtp-host">SMTP host</Label>
              <Input id="smtp-host" value={smtpHost} onChange={(e) => setSmtpHost(e.target.value)} placeholder="smtp.example.com" data-testid="input-smtp-host" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="smtp-port">Port</Label>
              <Input id="smtp-port" type="number" value={smtpPort} onChange={(e) => setSmtpPort(Number(e.target.value) || 587)} data-testid="input-smtp-port" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="smtp-user">Username</Label>
              <Input id="smtp-user" value={smtpUser} onChange={(e) => setSmtpUser(e.target.value)} data-testid="input-smtp-user" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="smtp-pass">Password</Label>
              <Input id="smtp-pass" type="password" value={smtpPass} onChange={(e) => setSmtpPass(e.target.value)} placeholder={row.smtpPasswordHint || "••••••••"} data-testid="input-smtp-pass" />
            </div>
            <div className="flex items-center gap-2 sm:col-span-2">
              <Switch id="smtp-ssl" checked={smtpSsl} onCheckedChange={setSmtpSsl} data-testid="switch-smtp-ssl" />
              <Label htmlFor="smtp-ssl">Use SSL / STARTTLS</Label>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="button-email-save">
            {save.isPending ? "Saving…" : "Save"}
          </Button>
          {row.source === "database" && (
            <Button variant="ghost" onClick={() => clear.mutate()} disabled={clear.isPending} data-testid="button-email-clear">
              <Trash2 className="mr-2 h-4 w-4" />
              Clear (revert to env)
            </Button>
          )}
        </div>

        <div className="border-t pt-4 space-y-2">
          <Label htmlFor="email-test">Send test email to</Label>
          <div className="flex gap-2">
            <Input id="email-test" type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@example.com" data-testid="input-email-test" />
            <Button variant="outline" onClick={() => test.mutate()} disabled={test.isPending || !testTo} data-testid="button-email-test">
              <Send className="mr-2 h-4 w-4" />
              {test.isPending ? "Sending…" : "Send"}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
